import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { ReleaseInfoCard } from '@components/SongCards/ReleaseInfoCard';
import { CarouselWithHeader } from '@components/main-components/carousel/HeaderCarousel';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  MoreFromArtistCarouselInfoFragmentDoc,
  PeopleAlsoCollectedCarouselDocument,
  useQuery,
} from '@graphql/reactQuery';
import { homePlaylist } from '@store/audio/AudioQueue';
import { getNextTrackIds } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PeopleAlsoCollectedCarousel($releaseId: UUID!, $userId: UUID) {
    releasesCollectorsAlsoMinted(releaseId: $releaseId) {
      id
      track {
        id
      }
      ...ReleaseInfoCard
    }
  }
`);

export const PeopleAlsoCollectedCarousel = ({
  artistFrag,
  releaseId,
  titleSlug,
}: {
  artistFrag: FragmentType<MoreFromArtistCarouselInfoFragmentDoc>;
  releaseId: string;
  titleSlug: string;
}) => {
  const { authUser } = useAuth();
  const { user } = getFragment(MoreFromArtistCarouselInfoFragmentDoc, artistFrag);

  const seeAllPath: Pathname = `/${titleSlug}/people-also-collected`;

  const { data: collectedReleases } = useQuery(PeopleAlsoCollectedCarouselDocument, {
    variables: {
      releaseId,
      userId: authUser?.id,
    },
    staleTime: 0,
  });

  const releases = collectedReleases?.data.releasesCollectorsAlsoMinted;

  const collectedReleaseSlides = React.useMemo(() => {
    return releases?.map(release => ({
      id: release.id,
      slide: (
        <ReleaseInfoCard
          data={release}
          playlist={homePlaylist}
          getNextTrackIds={getNextTrackIds({ trackId: release.track.id, releases: releases })}
        />
      ),
    }));
  }, [releases]);

  if (!collectedReleaseSlides || collectedReleaseSlides.length === 0) {
    return null;
  }

  return (
    <div className="relative mx-auto w-full overflow-hidden">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <CarouselWithHeader
          user={user}
          header="People also collected"
          subheader={null}
          slides={collectedReleaseSlides}
          path={seeAllPath}
          seeAllParams={null}
        />
      </div>
    </div>
  );
};
